import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Phone, Mail, MapPin, FolderKanban, ArrowRight, Plus, Building2, Users2, RefreshCw, ShieldCheck, ShieldAlert, Clock3 } from "lucide-react";
import { usePartner } from "../PartnerContext";
import { AppointmentsPanel } from "./PartnerProspects";
import { Card, PageHeader, Btn, SlideOver, InfoRow, Pill, TypeBadge, inputCls, Field } from "../ui";

const ACTIVATION_LABELS = {
  ACTIVE: { label: "Compte activé", tone: "sage", icon: ShieldCheck },
  INVITATION_ENVOYEE: { label: "Invitation envoyée", tone: "petrol", icon: Clock3 },
  INVITATION_EXPIREE: { label: "Invitation expirée", tone: "red", icon: ShieldAlert },
  AUCUN_COMPTE: { label: "Aucun accès Espace Client", tone: "stone", icon: ShieldAlert },
};

const EMPTY_PROJECT_FORM = { project_type: "", address: "", program: "" };
const EMPTY_SMALL_WORK_FORM = { address: "", need: "" };

const NewProjectForm = ({ clientId, onDone }) => {
  const { createProjectForClient } = usePartner();
  const [form, setForm] = useState(EMPTY_PROJECT_FORM);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const project = await createProjectForClient(clientId, form);
    setSaving(false);
    if (project) onDone();
  };
  return (
    <form onSubmit={submit} data-testid="client-new-project-form" className="mt-3 space-y-3 rounded-xl border border-[#E3E8E9] bg-petrol-bg/30 p-4">
      <Field label="Type de projet"><input data-testid="client-new-project-type" className={inputCls} value={form.project_type} onChange={(e) => setForm({ ...form, project_type: e.target.value })} placeholder="Construction, extension, rénovation…" /></Field>
      <Field label="Adresse du chantier"><input data-testid="client-new-project-address" className={inputCls} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
      <Field label="Programme / besoin"><textarea data-testid="client-new-project-program" rows={2} className={inputCls} value={form.program} onChange={(e) => setForm({ ...form, program: e.target.value })} /></Field>
      <div className="flex gap-2">
        <Btn type="submit" testid="client-new-project-submit" disabled={saving}>Créer le projet</Btn>
        <Btn type="button" variant="ghost" onClick={onDone}>Annuler</Btn>
      </div>
    </form>
  );
};

const NewSmallWorkForm = ({ clientId, onDone }) => {
  const { createSmallWorkForClient } = usePartner();
  const [form, setForm] = useState(EMPTY_SMALL_WORK_FORM);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const sw = await createSmallWorkForClient(clientId, form);
    setSaving(false);
    if (sw) onDone();
  };
  return (
    <form onSubmit={submit} data-testid="client-new-small-work-form" className="mt-3 space-y-3 rounded-xl border border-[#E3E8E9] bg-cognac/5 p-4">
      <Field label="Besoin"><textarea data-testid="client-new-small-work-need" rows={2} className={inputCls} value={form.need} onChange={(e) => setForm({ ...form, need: e.target.value })} placeholder="Remplacement fenêtres, peinture…" /></Field>
      <Field label="Adresse"><input data-testid="client-new-small-work-address" className={inputCls} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
      <div className="flex gap-2">
        <Btn type="submit" testid="client-new-small-work-submit" disabled={saving}>Créer le dossier</Btn>
        <Btn type="button" variant="ghost" onClick={onDone}>Annuler</Btn>
      </div>
    </form>
  );
};

const ClientDetail = ({ client }) => {
  const { territorialProspects, resendClientActivation } = usePartner();
  const [addingProject, setAddingProject] = useState(false);
  const [addingSmallWork, setAddingSmallWork] = useState(false);
  const [resending, setResending] = useState(false);
  const activation = ACTIVATION_LABELS[client.activation_status] || ACTIVATION_LABELS.AUCUN_COMPTE;
  const ActivationIcon = activation.icon;
  const linkedProspect = (territorialProspects || []).find((p) => p.clientProfileId === client.id);

  const resend = async () => {
    setResending(true);
    await resendClientActivation(client.id);
    setResending(false);
  };

  return (
    <>
      <div>
        {client.customer_type === "COMPANY" ? (
          <InfoRow label="Type" value={<span className="inline-flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" /> Entreprise — {client.company_name || client.name}</span>} />
        ) : (
          client.co_owner_first_name || client.co_owner_last_name ? (
            <InfoRow label="Co-acquéreur" value={<span className="inline-flex items-center gap-1.5"><Users2 className="h-3.5 w-3.5" /> {client.co_owner_first_name} {client.co_owner_last_name}</span>} />
          ) : null
        )}
        <InfoRow label="Commune" value={client.city} />
        <InfoRow label="Téléphone" value={client.phone} />
        <InfoRow label="Email" value={client.email || "—"} />
        <InfoRow label="Dernier contact" value={client.lastContact} />
        <InfoRow label="Prochaine action" value={client.nextAction} />
      </div>

      <div className="mt-6 rounded-xl border border-[#E3E8E9] p-4" data-testid={`client-activation-panel-${client.id}`}>
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-400">Accès Espace Client</p>
        <div className="mt-2 flex items-center justify-between gap-3">
          <Pill tone={activation.tone}><ActivationIcon className="h-3 w-3" /> {activation.label}</Pill>
          {activation.status !== "ACTIVE" && client.activation_status !== "ACTIVE" && client.email && (
            <Btn variant="ghost" testid={`client-resend-activation-${client.id}`} disabled={resending} onClick={resend}>
              <RefreshCw className="h-3.5 w-3.5" /> Renvoyer l'email d'activation
            </Btn>
          )}
        </div>
        {!client.email && <p className="mt-2 text-xs text-amber-700">Une adresse email est nécessaire pour créer l'accès à l'Espace Client.</p>}
      </div>

      {linkedProspect && (
        <div className="mt-6">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-400">Rendez-vous</p>
          <AppointmentsPanel prospectId={linkedProspect.id} />
        </div>
      )}

      <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.2em] text-stone-400">Dossiers ({client.projects_detail.length + client.small_works_detail.length})</p>
      <ul className="mt-2 space-y-2">
        {client.projects_detail.map((p) => (
          <li key={p.id}>
            <Link to={`/partenaire/projets/${p.reference}`} data-testid={`client-project-${p.reference}`} className="flex items-center justify-between gap-3 rounded-xl border border-[#E3E8E9] p-3 transition-colors hover:border-petrol/40">
              <span className="min-w-0">
                <span className="block font-mono text-[11px] font-bold text-petrol">{p.reference}</span>
                <span className="block truncate text-sm font-semibold text-navy">{p.name}</span>
                <span className="block text-xs text-stone-500">{p.phase}</span>
              </span>
              <TypeBadge type="moe" />
            </Link>
          </li>
        ))}
        {client.small_works_detail.map((s) => (
          <li key={s.id}>
            <Link to="/partenaire/petits-travaux" data-testid={`client-small-work-${s.reference}`} className="flex items-center justify-between gap-3 rounded-xl border border-[#E3E8E9] p-3 transition-colors hover:border-petrol/40">
              <span className="min-w-0">
                <span className="block font-mono text-[11px] font-bold text-petrol">{s.reference}</span>
                <span className="block truncate text-sm font-semibold text-navy">{s.need || "Petits travaux"}</span>
                <span className="block text-xs text-stone-500">{s.step}</span>
              </span>
              <TypeBadge type="small" />
            </Link>
          </li>
        ))}
        {client.projects_detail.length === 0 && client.small_works_detail.length === 0 && (
          <li data-testid={`client-no-dossier-${client.id}`} className="rounded-xl border border-dashed border-[#E3E8E9] p-4 text-center text-xs text-stone-500">
            Aucun dossier pour ce client pour le moment. Créez un projet ou un dossier Petits travaux quand vous êtes prêt.
          </li>
        )}
      </ul>

      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        <Btn variant="soft" testid={`client-add-project-${client.id}`} onClick={() => { setAddingProject((v) => !v); setAddingSmallWork(false); }}>
          <Plus className="h-4 w-4" /> Créer un projet
        </Btn>
        <Btn variant="soft" testid={`client-add-small-work-${client.id}`} onClick={() => { setAddingSmallWork((v) => !v); setAddingProject(false); }}>
          <Plus className="h-4 w-4" /> Créer un dossier Petits travaux
        </Btn>
      </div>
      {addingProject && <NewProjectForm clientId={client.id} onDone={() => setAddingProject(false)} />}
      {addingSmallWork && <NewSmallWorkForm clientId={client.id} onDone={() => setAddingSmallWork(false)} />}
    </>
  );
};

export default function PartnerClients() {
  const [q, setQ] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const { clients } = usePartner();
  const openId = searchParams.get("open");
  const selected = clients.find((c) => c.id === openId) || null;

  const list = useMemo(
    () => clients.filter((c) => !q || `${c.name} ${c.city}`.toLowerCase().includes(q.toLowerCase())),
    [clients, q]
  );

  const close = () => setSearchParams({});

  return (
    <div data-testid="partner-clients">
      <PageHeader
        eyebrow="Portefeuille"
        title="Clients"
        sub="Vos clients validés et leurs dossiers réunis au même endroit. Un client peut avoir plusieurs dossiers (Projet et/ou Petits travaux)."
      />

      <Card className="p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
          <input data-testid="client-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un client…" className={`${inputCls} pl-9`} />
        </div>
      </Card>

      <div data-testid="client-list" className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {list.map((c) => {
          const dossierCount = c.projects_detail.length + c.small_works_detail.length;
          return (
            <button
              key={c.id}
              data-testid={`client-card-${c.id}`}
              onClick={() => setSearchParams({ open: c.id })}
              className="rounded-2xl border border-[#E3E8E9] bg-white p-5 text-left transition-colors hover:border-petrol/40"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-semibold text-navy">{c.name}</p>
                  <p className="mt-0.5 flex items-center gap-1 text-xs text-stone-500"><MapPin className="h-3 w-3" /> {c.city || "—"}</p>
                </div>
                <Pill tone="petrol"><FolderKanban className="h-3 w-3" /> {dossierCount} dossier{dossierCount > 1 ? "s" : ""}</Pill>
              </div>
              <div className="mt-4 space-y-1 text-xs text-stone-600">
                <p className="flex items-center gap-1.5"><Phone className="h-3 w-3 text-stone-400" /> {c.phone}</p>
                <p className="flex items-center gap-1.5 truncate"><Mail className="h-3 w-3 text-stone-400" /> {c.email || "—"}</p>
              </div>
              <div className="mt-4 border-t border-[#F0F2F3] pt-3 text-xs">
                <p className="text-stone-400">Dernier contact : <span className="text-stone-600">{c.lastContact}</span></p>
                <p className="mt-1.5 flex items-center gap-1.5 font-semibold text-petrol"><ArrowRight className="h-3.5 w-3.5" /> {c.nextAction}</p>
              </div>
            </button>
          );
        })}
        {list.length === 0 && <Card className="p-10 text-center text-sm text-stone-500 md:col-span-3">Aucun client trouvé.</Card>}
      </div>

      <SlideOver open={!!selected} onClose={close} testid="client-detail" eyebrow="Fiche client" title={selected?.name}>
        {selected && <ClientDetail client={selected} />}
      </SlideOver>
    </div>
  );
}
